import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { CapabilityProvider } from '@lemontaigu/platform-ui';
import { createSupabaseCapabilityStore } from '@lemontaigu/platform/adapters/client';
import { supabase } from './supabase.js';

/**
 * The capability composition root for the back-office (ADR-0013, ladder step 8).
 *
 * A `*Provider.tsx`-shaped file by convention, because that is what
 * eslint.config.js recognises as a composition root — the one kind of file
 * permitted to bind a concrete client to a port. `CapabilityProvider` itself
 * names no infrastructure; this does.
 *
 * Mounted INSIDE `AuthGate`, deliberately: `my_capabilities()` resolves through
 * `auth.uid()`, so it is only meaningful once a session exists. Mounted above
 * the views so the fetch happens ONCE for the whole cockpit rather than per tab.
 */
export function AppCapabilityProvider({ children }: { children: ReactNode }) {
  // Stable across renders — CapabilityProvider keys its one fetch on this
  // identity, so a new instance per render would re-fetch on every render.
  const store = useMemo(() => createSupabaseCapabilityStore(supabase), []);
  return <CapabilityProvider store={store}>{children}</CapabilityProvider>;
}
